/**
 * WorkerJobFeed.js - Available jobs marketplace feed
 * Main screen where workers discover and pick up jobs
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, Filter, RefreshCw, MapPin, Calendar, DollarSign,
  Shirt, Truck, Car, Home, ShoppingBag, SlidersHorizontal, X,
  ChevronDown, AlertCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { jobsAPI } from '../../services/api';
import WorkerJobCard from './WorkerJobCard';
import WorkerJobDetails from './WorkerJobDetails';

const SERVICE_FILTERS = [
  { id: 'all', label: 'All', icon: null },
  { id: 'rides', label: 'Rides', icon: Car },
  { id: 'laundry', label: 'Laundry', icon: Shirt },
  { id: 'courier', label: 'Courier', icon: Truck },
  { id: 'errands', label: 'Errands', icon: ShoppingBag }
];

const DATE_FILTERS = [
  { id: 'all', label: 'Any Date' },
  { id: 'today', label: 'Today' },
  { id: 'tomorrow', label: 'Tomorrow' },
  { id: 'week', label: 'This Week' }
];

const SORT_OPTIONS = [
  { id: 'date', label: 'Date (Soonest)' },
  { id: 'pay_high', label: 'Pay (Highest)' },
  { id: 'pay_low', label: 'Pay (Lowest)' },
  { id: 'distance', label: 'Distance (Nearest)' }
];

const WorkerJobFeed = ({ onJobPickedUp, workerServiceTypes = [] }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [weeklyHours, setWeeklyHours] = useState(null);
  
  // Filters
  const [serviceFilter, setServiceFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Selected job for details modal
  const [selectedJob, setSelectedJob] = useState(null);
  const [pickupLoading, setPickupLoading] = useState(false);
  
  const fetchJobs = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    else setLoading(true);
    
    try {
      const params = {};
      if (serviceFilter !== 'all') {
        params.service_type = serviceFilter;
      }
      
      // Date filtering
      const today = new Date().toISOString().split('T')[0];
      if (dateFilter === 'today') {
        params.date_from = today;
        params.date_to = today;
      } else if (dateFilter === 'tomorrow') {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        const tomorrowStr = tomorrow.toISOString().split('T')[0];
        params.date_from = tomorrowStr;
        params.date_to = tomorrowStr;
      } else if (dateFilter === 'week') {
        const weekEnd = new Date();
        weekEnd.setDate(weekEnd.getDate() + 7);
        params.date_from = today;
        params.date_to = weekEnd.toISOString().split('T')[0];
      }
      
      const response = await jobsAPI.getAvailable(params);
      let fetchedJobs = response.data?.jobs || [];
      setWeeklyHours(response.data?.weekly_hours || null);
      
      // Client-side sorting
      if (sortBy === 'pay_high') {
        fetchedJobs.sort((a, b) => (b.worker_pay || 0) - (a.worker_pay || 0));
      } else if (sortBy === 'pay_low') {
        fetchedJobs.sort((a, b) => (a.worker_pay || 0) - (b.worker_pay || 0));
      } else if (sortBy === 'distance') {
        fetchedJobs.sort((a, b) => (a.distance_miles || 999) - (b.distance_miles || 999));
      }
      // Default is date (already sorted from backend)
      
      setJobs(fetchedJobs);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
      toast.error('Failed to load available jobs');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [serviceFilter, dateFilter, sortBy]);
  
  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);
  
  const handlePickupJob = async (job) => {
    setPickupLoading(true);
    try {
      const response = await jobsAPI.claimJob(job.id);
      toast.success(response.data?.message || 'Job assigned to you! Check My Jobs.');
      setSelectedJob(null);
      onJobPickedUp?.(job);
      // Refresh the list
      fetchJobs(true);
    } catch (error) {
      console.error('Failed to pick up job:', error);
      toast.error(error.response?.data?.detail || 'Failed to pick up job');
    } finally {
      setPickupLoading(false);
    }
  };
  
  // Filter jobs by search query
  const filteredJobs = jobs.filter(job => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      job.service_name?.toLowerCase().includes(query) ||
      job.service_type?.toLowerCase().includes(query) ||
      job.city?.toLowerCase().includes(query) ||
      job.address?.toLowerCase().includes(query)
    );
  });
  
  // Separate urgent jobs
  const urgentJobs = filteredJobs.filter(j => j.urgency_level === 'urgent' || j.is_urgent);
  const regularJobs = filteredJobs.filter(j => j.urgency_level !== 'urgent' && !j.is_urgent);
  
  return (
    <div className="min-h-screen bg-[var(--background)]">
      {/* Empty Service Types Warning Banner */}
      {(!workerServiceTypes || workerServiceTypes.length === 0) && (
        <div className="bg-amber-500/15 border-b border-amber-500/30 px-4 py-3" data-testid="empty-service-alert">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-amber-400 font-medium text-sm">
                No service types configured
              </p>
              <p className="text-amber-400/80 text-xs mt-0.5">
                You're seeing all available jobs. Contact your manager to set your service types for a filtered job feed.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Header */}
      <div className="sticky top-0 z-30 bg-[var(--background-paper)] border-b border-[var(--border-primary)]">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h1 className="text-xl font-bold text-[var(--text-primary)]">Available Jobs</h1>
              {weeklyHours && (
                <p className="text-sm text-[var(--text-muted)]">
                  {(weeklyHours.total_hours || weeklyHours.used_hours || 0).toFixed(1)} / {weeklyHours.weekly_cap || 40} hrs this week
                </p>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchJobs(true)}
              disabled={refreshing}
              className="border-[var(--primary)] text-[var(--primary)]"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
          
          {/* Search */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search jobs..."
              className="pl-10 bg-[var(--background-subtle)]"
              data-testid="job-search"
            />
          </div>
          
          {/* Service Type Pills */}
          <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
            {SERVICE_FILTERS.map(filter => {
              const Icon = filter.icon;
              const isActive = serviceFilter === filter.id;
              const isAvailable = filter.id === 'all' || workerServiceTypes.includes(filter.id);
              
              return (
                <button
                  key={filter.id}
                  onClick={() => isAvailable && setServiceFilter(filter.id)}
                  disabled={!isAvailable}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                    isActive
                      ? 'bg-[var(--primary)] text-black'
                      : isAvailable
                        ? 'bg-[var(--background-subtle)] text-[var(--text-secondary)] hover:bg-[var(--background-subtle)]/80'
                        : 'bg-[var(--background-subtle)] text-[var(--text-muted)] opacity-50 cursor-not-allowed'
                  }`}
                  data-testid={`filter-${filter.id}`}
                >
                  {Icon && <Icon className="w-3.5 h-3.5" />}
                  {filter.label}
                </button>
              );
            })}
          </div>
          
          {/* Additional Filters Toggle */}
          <div className="flex items-center justify-between pt-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-1 text-sm text-[var(--text-muted)] hover:text-[var(--primary)]"
            >
              <SlidersHorizontal className="w-4 h-4" />
              More Filters
              <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>
            <p className="text-sm text-[var(--text-muted)]">
              {filteredJobs.length} job{filteredJobs.length !== 1 ? 's' : ''} available
            </p>
          </div>
          
          {/* Expanded Filters */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="pt-3 space-y-3">
                  {/* Date Filter */}
                  <div>
                    <label className="text-xs text-[var(--text-muted)] uppercase tracking-wide mb-1 block">
                      Date
                    </label>
                    <div className="flex gap-2 flex-wrap">
                      {DATE_FILTERS.map(filter => (
                        <button
                          key={filter.id}
                          onClick={() => setDateFilter(filter.id)}
                          className={`px-3 py-1 rounded-lg text-sm ${
                            dateFilter === filter.id
                              ? 'bg-[var(--primary)] text-black'
                              : 'bg-[var(--background-subtle)] text-[var(--text-secondary)]'
                          }`}
                        >
                          {filter.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Sort */}
                  <div>
                    <label className="text-xs text-[var(--text-muted)] uppercase tracking-wide mb-1 block">
                      Sort By
                    </label>
                    <div className="flex gap-2 flex-wrap">
                      {SORT_OPTIONS.map(option => (
                        <button
                          key={option.id}
                          onClick={() => setSortBy(option.id)}
                          className={`px-3 py-1 rounded-lg text-sm ${
                            sortBy === option.id
                              ? 'bg-[var(--primary)] text-black'
                              : 'bg-[var(--background-subtle)] text-[var(--text-secondary)]'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      
      {/* Job List */}
      <div className="p-4 pb-24 space-y-4">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-10 h-10 border-3 border-[var(--primary)] border-t-transparent rounded-full animate-spin mb-4" />
            <p className="text-[var(--text-muted)]">Loading available jobs...</p>
          </div>
        ) : filteredJobs.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 bg-[var(--background-subtle)] rounded-full flex items-center justify-center">
              <Search className="w-8 h-8 text-[var(--text-muted)]" />
            </div>
            <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">
              No Jobs Available
            </h3>
            <p className="text-[var(--text-muted)] mb-4">
              {searchQuery
                ? 'No jobs match your search. Try different keywords.'
                : 'Check back soon for new opportunities!'}
            </p>
            <Button variant="outline" onClick={() => fetchJobs(true)}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        ) : (
          <>
            {/* Urgent Jobs Section */}
            {urgentJobs.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                  <h2 className="font-bold text-red-500">URGENT JOBS</h2>
                </div>
                <div className="space-y-3">
                  {urgentJobs.map(job => (
                    <WorkerJobCard
                      key={job.id}
                      job={job}
                      onSelect={setSelectedJob}
                      onPickup={handlePickupJob}
                    />
                  ))}
                </div>
              </div>
            )}
            
            {/* Regular Jobs */}
            {regularJobs.length > 0 && (
              <div className="space-y-3">
                {urgentJobs.length > 0 && (
                  <h2 className="font-semibold text-[var(--text-secondary)] mb-2">
                    ALL AVAILABLE JOBS
                  </h2>
                )}
                {regularJobs.map(job => (
                  <WorkerJobCard
                    key={job.id}
                    job={job}
                    onSelect={setSelectedJob}
                    onPickup={handlePickupJob}
                    compact={true}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Job Details Modal */}
      <WorkerJobDetails
        job={selectedJob}
        isOpen={!!selectedJob}
        onClose={() => setSelectedJob(null)}
        onPickup={handlePickupJob}
        isLoading={pickupLoading}
      />
    </div>
  );
};

export default WorkerJobFeed;
